import React from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { TRY } from "@/lib/api";
import { Card } from "@/components/Cards";

const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const label = (m) => `${MONTHS[parseInt(m.slice(5), 10) - 1]} ${m.slice(0, 4)}`;

const allMonths = (start, end) => { const out = []; let [y, m] = start.split("-").map(Number); const [ey, em] = end.split("-").map(Number); while (y < ey || (y === ey && m <= em)) { out.push(`${y}-${String(m).padStart(2, "0")}`); m++; if (m > 12) { m = 1; y++; } } return out; };

export default function CashMonthlyCompare({ summary }) {
  const byM = Object.fromEntries(summary.by_month.map((m) => [m.month, m]));
  const rows = allMonths(summary.start.slice(0, 7), summary.end.slice(0, 7)).map((m) => byM[m] || { month: m, income: 0, expense: 0, net: 0 });
  const max = Math.max(1, ...rows.map((r) => Math.max(r.income, r.expense)));
  const avgInc = rows.reduce((s, r) => s + r.income, 0) / rows.length;
  const avgExp = rows.reduce((s, r) => s + r.expense, 0) / rows.length;
  const best = rows.reduce((a, r) => (r.net > (a?.net ?? -Infinity) ? r : a), null);
  const worst = rows.reduce((a, r) => (r.net < (a?.net ?? Infinity) ? r : a), null);

  return (
    <Card className="overflow-hidden" data-testid="cashbook-yearly">
      <div className="p-5 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div><h3 className="font-heading text-lg font-semibold">Aylık Karşılaştırma</h3><p className="text-xs text-muted-foreground">{rows.length} ay · Ortalama gelir <span className="font-mono text-emerald-400">{TRY(avgInc)}</span> · Ortalama gider <span className="font-mono text-rose-400">{TRY(avgExp)}</span></p></div>
        {best && best.net !== 0 && <div className="text-xs text-muted-foreground">En iyi ay <b className="text-emerald-400">{label(best.month)}</b> · En zayıf ay <b className="text-rose-400">{label(worst.month)}</b></div>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-y border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Ay</th><th className="px-4 py-2.5 w-[32%]">Gelir / Gider</th><th className="px-4 py-2.5 text-right">Gelir</th><th className="px-4 py-2.5 text-right">Gider</th><th className="px-4 py-2.5 text-right">Net</th><th className="px-4 py-2.5 text-right">Önceki Aya Göre</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const prev = rows[i - 1];
              const diff = prev ? r.net - prev.net : null;
              return (
                <tr key={r.month} className="border-b border-border/60" data-testid={`cashbook-year-row-${r.month}`}>
                  <td className="px-4 py-2 font-medium whitespace-nowrap">{label(r.month)}</td>
                  <td className="px-4 py-2">
                    <div className="space-y-1">
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-400/80" style={{ width: `${(r.income / max) * 100}%` }} /></div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-rose-400/80" style={{ width: `${(r.expense / max) * 100}%` }} /></div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-400">{TRY(r.income)}</td>
                  <td className="px-4 py-2 text-right font-mono text-rose-400">{TRY(r.expense)}</td>
                  <td className={`px-4 py-2 text-right font-mono font-semibold ${r.net >= 0 ? "" : "text-rose-400"}`}>{TRY(r.net)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {diff === null ? <span className="text-muted-foreground">—</span> : (
                      <span className={`inline-flex items-center gap-1 ${diff > 0 ? "text-emerald-400" : diff < 0 ? "text-rose-400" : "text-muted-foreground"}`}>
                        {diff > 0 ? <ArrowUpRight className="h-3 w-3" /> : diff < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}{TRY(Math.abs(diff))}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot><tr className="bg-muted/30 font-semibold">
            <td className="px-4 py-2.5" colSpan={2}>TOPLAM</td>
            <td className="px-4 py-2.5 text-right font-mono text-emerald-400" data-testid="cashbook-year-income">{TRY(summary.income)}</td>
            <td className="px-4 py-2.5 text-right font-mono text-rose-400" data-testid="cashbook-year-expense">{TRY(summary.expense)}</td>
            <td className={`px-4 py-2.5 text-right font-mono ${summary.net >= 0 ? "" : "text-rose-400"}`} data-testid="cashbook-year-net">{TRY(summary.net)}</td><td />
          </tr></tfoot>
        </table>
      </div>
    </Card>
  );
}
